import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material'
import { useState } from 'react'
import { type CollectionDistrict } from '../../../../../models/peerToPeer/Collection'
import { SetP2PProgramStatus } from '../../../../../services/peerToPeer/PeerToPeerService'
import { WizardFooter } from './WizardFooter'
import { allWritesSucceeded } from './programStatusWrites'
import { stepStyles } from './stepStyles'

interface Props {
  district?: CollectionDistrict
  buildingIds: number[]
  reportingYearId: number
  onBack: () => void
  onContinue: (withProgramBuildingIds: number[], withoutProgramBuildingIds: number[]) => void
}

export const ProgramStatusStep = ({ district, buildingIds, reportingYearId, onBack, onContinue }: Props): JSX.Element => {
  const buildings = (district?.buildings ?? []).filter(b => buildingIds.includes(b.organizationBuildingId))
  const [withProgram, setWithProgram] = useState<Set<number>>(
    () => new Set(buildings.filter(b => b.hasActiveProgram === true).map(b => b.organizationBuildingId))
  )
  const [isSaving, setIsSaving] = useState<boolean>(false)

  const toggle = (id: number): void => {
    setWithProgram(previous => {
      const next = new Set(previous)
      if (next.has(id)) { next.delete(id) } else { next.add(id) }
      return next
    })
  }

  const storedProgramStatus = (id: number): boolean | null =>
    buildings.find(b => b.organizationBuildingId === id)?.hasActiveProgram ?? null

  const persistAndContinue = async (): Promise<void> => {
    setIsSaving(true)
    // Only buildings whose answer changed (or that have no form row yet) are written, via the
    // merge-only endpoint, so a re-pass through Program Status never clobbers program details
    // already entered for an unchanged building.
    const changed = buildingIds.filter(id => {
      const stored = storedProgramStatus(id)
      return stored == null || stored !== withProgram.has(id)
    })
    const results = await Promise.all(changed.map(async id =>
      await SetP2PProgramStatus({ organizationBuildingId: id, reportingYearId, hasActiveProgram: withProgram.has(id) })
    ))
    setIsSaving(false)
    if (!allWritesSucceeded(results)) return

    onContinue(buildingIds.filter(id => withProgram.has(id)), buildingIds.filter(id => !withProgram.has(id)))
  }

  return (
    <Box data-testid='p2pProgramStatusStep'>
      <Typography variant='h3'>Program Status</Typography>
      <Typography variant='body2' sx={stepStyles.intro}>Help us understand which of your selected buildings have an active program.</Typography>

      <Box sx={stepStyles.section}>
        <Typography variant='h4'>Peer to Peer Programs</Typography>
        <Box sx={stepStyles.formField}>
          <Box sx={stepStyles.fieldText}>
            <Typography variant='body1'>Which buildings have a Peer to Peer program?</Typography>
            <Typography variant='body2'>Select all that apply.</Typography>
          </Box>
          <Box sx={stepStyles.checkList}>
            {buildings.map(building => (
              <Box key={building.organizationBuildingId} sx={stepStyles.checkRow}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={withProgram.has(building.organizationBuildingId)}
                      onChange={() => { toggle(building.organizationBuildingId) }}
                      data-testid={`p2pProgramCheck-${building.organizationBuildingId}`}
                    />
                  }
                  label={building.name}
                  slotProps={{ typography: { sx: stepStyles.checkLabel } }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <WizardFooter
        onBack={onBack}
        onContinue={() => { void persistAndContinue() }}
        continueDisabled={isSaving}
        backTestId='p2pProgramStatusBack'
        continueTestId='p2pProgramStatusContinue'
      />
    </Box>
  )
}
